import { useMemo, useState, type FormEvent } from 'react';
import { mensajeDe, type Categoria, type ParejaAdmin } from '../../services/api';
import { useClienteTorneo } from '../../services/clienteTorneo';
import { Aviso, Boton, Tarjeta, Titulo } from '../../components/ui';
import { nombrePareja, VistaGrupo } from '../../components/Grupos';
import { completarGrupos, describirOpcion, letraDeGrupo, opcionesDeGrupos, problemaConReparto, sortearGrupos } from '../../nucleo/grupos';
import { validarMarcador, type Marcador } from '../../nucleo/marcador';
import { conTabla, marcadorDeFila } from '../../nucleo/vistaGrupos';

// La fase de grupos desde el lado del organizador: armar los grupos (con el
// tamaño que él elige), cargar resultados y, si hace falta, el sorteo.

export function SeccionGrupos({ categoria, recargar, horariosActivos = true }: { horariosActivos?: boolean; categoria: Categoria<ParejaAdmin>; recargar: () => Promise<void> }) {
  if (categoria.grupos.length === 0) return <ArmarGrupos categoria={categoria} recargar={recargar} />;
  return <GruposArmados horariosActivos={horariosActivos} categoria={categoria} recargar={recargar} />;
}

// ── Armar ─────────────────────────────────────────────────────────────────

function ArmarGrupos({ categoria, recargar }: { categoria: Categoria<ParejaAdmin>; recargar: () => Promise<void> }) {
  const api = useClienteTorneo();
  const inscriptas = categoria.parejas.filter((p) => p.estado === 'inscripta');
  const pasan = categoria.pasan_por_grupo ?? 1;
  // Solo se ofrecen repartos donde cada grupo tiene más parejas de las que pasan.
  const opciones = opcionesDeGrupos(inscriptas.length).filter((o) => Math.min(...o) > pasan);
  const [abierto, setAbierto] = useState(false);
  const [opcion, setOpcion] = useState(0);
  // Los grupos como van quedando. Arrancan vacíos: el organizador puede ubicar
  // a mano las parejas que quiera y después completar el resto al azar.
  const [grupos, setGrupos] = useState<number[][]>([]);
  // Las que ubicó él (no el azar): quedan anotadas en el registro del torneo.
  const [aMano, setAMano] = useState<Set<number>>(new Set());
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  const porId = new Map(inscriptas.map((p) => [p.id, p]));
  const tamanios = opciones[opcion] ?? [];
  const cantidad = tamanios.length;
  // Siempre tantos grupos como dice la opción elegida.
  const vista = Array.from({ length: cantidad }, (_, i) => grupos[i] ?? []);
  const ubicadas = new Set(vista.flat());
  const sinGrupo = inscriptas.filter((p) => !ubicadas.has(p.id));

  const elegirOpcion = (i: number) => {
    // Al cambiar la cantidad de grupos, lo que estaba en los que desaparecen vuelve a "sin grupo".
    setOpcion(i);
    setGrupos((g) => g.slice(0, opciones[i]!.length));
  };

  /** Mover una pareja a un grupo (o sacarla: destino -1). Eso es "a mano". */
  const mover = (parejaId: number, destino: number) => {
    setGrupos(vista.map((g, i) => (i === destino ? [...g.filter((x) => x !== parejaId), parejaId] : g.filter((x) => x !== parejaId))));
    const nuevas = new Set(aMano);
    if (destino === -1) nuevas.delete(parejaId);
    else nuevas.add(parejaId);
    setAMano(nuevas);
    setError('');
  };

  const completar = () => {
    setGrupos(completarGrupos(inscriptas, tamanios, vista));
    setError('');
  };
  const sortearTodo = () => {
    setGrupos(sortearGrupos(inscriptas, tamanios));
    setAMano(new Set());
    setError('');
  };
  const vaciar = () => {
    setGrupos([]);
    setAMano(new Set());
  };

  const completo = sinGrupo.length === 0 && cantidad > 0;
  const problema = completo ? problemaConReparto(vista, inscriptas.map((p) => p.id), pasan) : null;

  const confirmar = async () => {
    setEnviando(true);
    try {
      await api.armarGrupos(categoria.id, vista, [...aMano].filter((id) => ubicadas.has(id)));
      await recargar();
    } catch (e) {
      setError(mensajeDe(e));
    } finally {
      setEnviando(false);
    }
  };

  if (inscriptas.length < 3) {
    return <p className="mt-3 text-sm text-(--color-apagado)">Con 3 parejas inscriptas o más se pueden armar los grupos.</p>;
  }
  if (!abierto) {
    return (
      <div className="mt-3">
        <Boton chico onClick={() => setAbierto(true)}>Armar grupos</Boton>
      </div>
    );
  }

  const selector = (id: number, actual: number) => (
    <select
      aria-label="Grupo"
      className="rounded-lg border border-(--color-borde) bg-(--color-panel) px-1 py-0.5 text-xs"
      value={actual}
      onChange={(e) => mover(id, Number(e.target.value))}
    >
      <option value={-1}>Sin grupo</option>
      {vista.map((_, j) => (
        <option key={j} value={j}>{letraDeGrupo(j)}</option>
      ))}
    </select>
  );
  const nombre = (id: number) => (
    <span className="min-w-0 flex-1">
      {nombrePareja(porId.get(id))}
      {porId.get(id)?.cabeza_serie != null && <span className="ml-1 text-xs text-(--color-acento)">CS {porId.get(id)!.cabeza_serie}</span>}
      {aMano.has(id) && <span className="ml-1 text-[10px] uppercase text-(--color-apagado)">a mano</span>}
    </span>
  );

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-xl border border-(--color-borde) p-3">
      <p className="text-sm font-semibold">
        {inscriptas.length} parejas inscriptas, pasan {pasan} por grupo. ¿Cómo querés los grupos?
      </p>
      {opciones.length === 0 ? (
        <Aviso tono="alerta">Con {inscriptas.length} parejas y pasando {pasan} por grupo no hay un reparto posible.</Aviso>
      ) : (
        <div className="flex flex-col gap-2" role="radiogroup">
          {opciones.map((o, i) => (
            <button
              key={o.join('-')}
              role="radio"
              aria-checked={opcion === i}
              onClick={() => elegirOpcion(i)}
              className={`min-h-11 rounded-xl border px-3 text-left text-sm ${
                opcion === i ? 'border-(--color-acento) font-semibold text-(--color-acento)' : 'border-(--color-borde) text-(--color-apagado)'
              }`}
            >
              {describirOpcion(o)}
            </button>
          ))}
        </div>
      )}

      {cantidad > 0 && (
        <>
          <p className="text-xs text-(--color-apagado)">
            Podés ubicar a mano las parejas que quieras (con el selector de cada una) y después tocar <b>Completar al azar</b>: reparte el
            resto sin mover las tuyas. O <b>Sortear todo</b> y listo. Las cabezas de serie van separadas.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {vista.map((g, i) => (
              <div key={i} className="rounded-xl border border-(--color-borde) p-2">
                <p className="mb-1 flex items-baseline gap-2 font-titulo text-xl uppercase">
                  Grupo {letraDeGrupo(i)}
                  <span className={`font-sans text-xs normal-case ${g.length === tamanios[i] ? 'text-(--color-apagado)' : 'text-(--color-alerta)'}`}>
                    {g.length} de {tamanios[i]}
                  </span>
                </p>
                <ul className="flex flex-col gap-1 text-sm">
                  {g.map((id) => (
                    <li key={id} className="flex items-center gap-2">
                      {nombre(id)}
                      {selector(id, i)}
                    </li>
                  ))}
                  {g.length === 0 && <li className="text-xs text-(--color-apagado)">vacío</li>}
                </ul>
              </div>
            ))}
          </div>

          {sinGrupo.length > 0 && (
            <div className="rounded-xl border border-dashed border-(--color-borde) p-2">
              <p className="mb-1 text-sm font-semibold text-(--color-apagado)">Sin grupo ({sinGrupo.length})</p>
              <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto text-sm">
                {sinGrupo.map((p) => (
                  <li key={p.id} className="flex items-center gap-2">
                    {nombre(p.id)}
                    {selector(p.id, -1)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {problema && <Aviso tono="alerta">{problema}</Aviso>}
      {error && <Aviso tono="error">{error}</Aviso>}

      <div className="flex flex-wrap gap-2">
        {cantidad > 0 && sinGrupo.length > 0 && (
          <Boton chico onClick={completar}>
            {ubicadas.size === 0 ? 'Sortear todo' : 'Completar al azar'}
          </Boton>
        )}
        {completo && (
          <Boton chico onClick={confirmar} deshabilitado={enviando || problema !== null}>
            {enviando ? 'Guardando...' : 'Confirmar grupos'}
          </Boton>
        )}
        {cantidad > 0 && ubicadas.size > 0 && (
          <>
            <Boton chico tipo="secundario" onClick={sortearTodo}>Sortear todo de nuevo</Boton>
            <Boton chico tipo="secundario" onClick={vaciar}>Vaciar</Boton>
          </>
        )}
        <Boton chico tipo="secundario" onClick={() => { setAbierto(false); vaciar(); }}>Cancelar</Boton>
      </div>
      <p className="text-xs text-(--color-apagado)">Los partidos de cada grupo se arman solos al confirmar.</p>
    </div>
  );
}

// ── Armados: tabla y resultados ───────────────────────────────────────────

function GruposArmados({ categoria, recargar, horariosActivos }: { horariosActivos: boolean; categoria: Categoria<ParejaAdmin>; recargar: () => Promise<void> }) {
  const api = useClienteTorneo();
  const [editando, setEditando] = useState<number | null>(null);
  const [error, setError] = useState('');

  const vista = useMemo(() => {
    const parejas = new Map(categoria.parejas.map((p) => [p.id, p]));
    const bajas = new Set(categoria.parejas.filter((p) => p.estado === 'baja').map((p) => p.id));
    return { parejas, grupos: categoria.grupos.map((g) => conTabla(g, bajas, categoria.pasan_por_grupo ?? 1)) };
  }, [categoria]);

  const hayResultados = categoria.grupos.some((g) => g.partidos.some((p) => p.estado === 'jugado'));

  const accion = async (hacer: () => Promise<unknown>) => {
    setError('');
    try {
      await hacer();
      await recargar();
    } catch (e) {
      setError(mensajeDe(e));
    }
  };

  return (
    <div className="mt-4 flex flex-col gap-3">
      {error && <Aviso tono="error">{error}</Aviso>}
      {vista.grupos.map((g) => (
        <VistaGrupo
          conHorarios={horariosActivos}
          key={g.id}
          grupo={g}
          parejas={vista.parejas}
          pasan={categoria.pasan_por_grupo ?? 1}
          extraPartido={(partidoId) => {
            const partido = g.partidos.find((p) => p.id === partidoId)!;
            const r = g.resultados.get(partidoId);
            if (r?.porAbandono) return null;
            if (editando === partidoId) {
              return (
                <FormResultado
                  a={nombrePareja(vista.parejas.get(partido.pareja_a!))}
                  b={nombrePareja(vista.parejas.get(partido.pareja_b!))}
                  inicial={marcadorDeFila(partido)}
                  alCancelar={() => setEditando(null)}
                  alGuardar={async (m) => {
                    await api.cargarResultado(partidoId, m);
                    setEditando(null);
                    await recargar();
                  }}
                  alAnular={
                    partido.estado === 'jugado'
                      ? async () => {
                          await api.anularResultado(partidoId);
                          setEditando(null);
                          await recargar();
                        }
                      : undefined
                  }
                />
              );
            }
            return (
              <button onClick={() => setEditando(partidoId)} className="mt-1 text-xs font-semibold text-(--color-acento)">
                {partido.estado === 'jugado' ? 'Corregir' : 'Cargar resultado'}
              </button>
            );
          }}
          plegable
          pie={
            g.tabla.faltaSorteo.length > 0 ? (
              <div className="mt-3">
                <Boton chico onClick={() => accion(() => api.sortearDesempate(g.id))}>
                  Sortear el desempate
                </Boton>
              </div>
            ) : null
          }
        />
      ))}
      {/* Con el cuadro armado, los grupos ya no se deshacen (el cuadro sale de ellos). */}
      {!hayResultados && !categoria.cuadro && (
        <Tarjeta>
          <Titulo>¿Te equivocaste?</Titulo>
          <p className="mb-3 text-sm text-(--color-apagado)">
            Mientras no haya ningún resultado cargado, los grupos se pueden deshacer y volver a armar.
          </p>
          <Boton chico tipo="peligro" onClick={() => accion(() => api.deshacerGrupos(categoria.id))}>
            Deshacer grupos
          </Boton>
        </Tarjeta>
      )}
    </div>
  );
}

// ── Cargar un resultado ───────────────────────────────────────────────────

/**
 * Pensado para usarlo parado al lado de la cancha: dos sets, y el súper
 * tiebreak aparece solo si quedaron 1 a 1. Valida con la misma función que la
 * API, así el error sale antes de mandar.
 */
export function FormResultado({
  a,
  b,
  inicial,
  alGuardar,
  alCancelar,
  alAnular,
}: {
  a: string;
  b: string;
  inicial: Marcador | null;
  alGuardar: (m: Marcador) => Promise<void>;
  alCancelar: () => void;
  alAnular?: () => Promise<void>;
}) {
  const aTexto = (n: number | undefined) => (n === undefined ? '' : String(n));
  const s = inicial?.sets;
  const [v, setV] = useState<string[]>([
    aTexto(s?.[0][0]), aTexto(s?.[0][1]), aTexto(s?.[1][0]), aTexto(s?.[1][1]), aTexto(inicial?.tb?.[0]), aTexto(inicial?.tb?.[1]),
  ]);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  const n = (i: number) => (v[i] === '' ? NaN : Number(v[i]));
  const setsCompletos = [0, 1, 2, 3].every((i) => v[i] !== '');
  const unoYUno = setsCompletos && Math.sign(n(0) - n(1)) !== Math.sign(n(2) - n(3));

  const armar = (): unknown =>
    unoYUno
      ? { sets: [[n(0), n(1)], [n(2), n(3)]], tb: [n(4), n(5)] }
      : { sets: [[n(0), n(1)], [n(2), n(3)]] };

  const mandar = async (cuerpo: unknown) => {
    const m = validarMarcador(cuerpo);
    if (!m.ok) return setError(m.error);
    setEnviando(true);
    setError('');
    try {
      await alGuardar(m.valor);
    } catch (e) {
      setError(mensajeDe(e));
    } finally {
      setEnviando(false);
    }
  };

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    void mandar(armar());
  };

  const NOMBRE_CELDA = ['1º set, de arriba', '1º set, de abajo', '2º set, de arriba', '2º set, de abajo', 'súper tiebreak, de arriba', 'súper tiebreak, de abajo'];
  const celda = (i: number) => (
    <input
      // Sin ENTRADA: trae w-full, y acá cada casilla tiene su ancho fijo.
      className="min-h-10 w-12 rounded-xl border border-(--color-borde) bg-(--color-panel) text-center text-base text-(--color-texto) focus:border-(--color-acento) focus:outline-none"
      inputMode="numeric"
      value={v[i]}
      aria-label={`Games del ${NOMBRE_CELDA[i]}`}
      onChange={(e) => setV(v.map((x, j) => (j === i ? e.target.value.replace(/\D/g, '').slice(0, 2) : x)))}
    />
  );

  return (
    <form onSubmit={enviar} className="mt-2 flex flex-col gap-2 rounded-xl border border-(--color-acento) p-3">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] items-center gap-2 text-sm">
        <span />
        <span className="w-12 text-center text-[11px] text-(--color-apagado)">1º set</span>
        <span className="w-12 text-center text-[11px] text-(--color-apagado)">2º set</span>
        <span className="w-12 text-center text-[11px] text-(--color-apagado)">{unoYUno ? 'Súper TB' : ''}</span>
        <span className="min-w-0 truncate font-semibold">{a}</span>
        {celda(0)}
        {celda(2)}
        {unoYUno ? celda(4) : <span />}
        <span className="min-w-0 truncate font-semibold">{b}</span>
        {celda(1)}
        {celda(3)}
        {unoYUno ? celda(5) : <span />}
      </div>
      {error && <Aviso tono="error">{error}</Aviso>}
      <div className="flex flex-wrap gap-2">
        <Boton chico type="submit" deshabilitado={enviando || !setsCompletos}>
          {enviando ? 'Guardando...' : 'Guardar'}
        </Boton>
        <Boton chico tipo="secundario" onClick={alCancelar}>Cancelar</Boton>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-(--color-borde) pt-2 text-xs text-(--color-apagado)">
        <span>¿No se presentó una pareja?</span>
        <Boton chico tipo="texto" onClick={() => mandar({ wo: 'a' })}>W.O. para la de arriba</Boton>
        <Boton chico tipo="texto" onClick={() => mandar({ wo: 'b' })}>W.O. para la de abajo</Boton>
        {alAnular && (
          <Boton chico tipo="peligro" onClick={() => alAnular().catch((e: unknown) => setError(mensajeDe(e)))}>
            Anular resultado
          </Boton>
        )}
      </div>
    </form>
  );
}
